/**
 * DateTimePicker — reusable date + time picker.
 *
 * One thin wrapper around a Radix Popover containing:
 *   - A calendar grid (Su–Sa, month-nav arrows, current day highlighted)
 *   - Two single-thumb sliders for Hour and Minute, with the formatted
 *     time displayed above
 *
 * The value is the string format <input type="datetime-local"> uses —
 * `YYYY-MM-DDTHH:MM` — so swapping this into a form that previously
 * used a `datetime-local` input is a drop-in replacement: same value
 * shape, same onChange signature.
 *
 * Why a custom widget instead of the browser's native datetime-local:
 *   1. Consistent look across browsers (Safari and Firefox render
 *      `datetime-local` very differently than Chrome).
 *   2. Per-row default-time hooks: a "from date" picker can default
 *      newly-picked dates to 12:00 am; a "to date" picker to 11:59 pm.
 *      Native pickers don't support that.
 *   3. Touch-first sliders for hour/minute — matches the in-house
 *      dual-thumb slider used elsewhere in this codebase.
 *
 * No new npm deps — uses the existing @radix-ui/react-popover and
 * lucide-react icons. Stays within CLAUDE.md Rule 22 (no installs
 * during the active supply-chain attack window).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

interface Parsed {
  year: number;
  month: number; // 1-12
  day: number;   // 1-31
  hour: number;  // 0-23
  minute: number; // 0-59
}

function pad2(n: number): string { return String(n).padStart(2, '0'); }

/** Parse "YYYY-MM-DDTHH:MM" (also accepts a space separator or bare date). */
function parseValue(s: string): Parsed {
  const t = (s || '').replace(' ', 'T');
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(t);
  if (!m) {
    const now = new Date();
    return {
      year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate(),
      hour: 0, minute: 0,
    };
  }
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: m[4] ? Number(m[4]) : 0,
    minute: m[5] ? Number(m[5]) : 0,
  };
}

function format(p: Parsed, dateOnly = false): string {
  const date = `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
  return dateOnly ? date : `${date}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** "2026-05-14T13:30" → "05/14/2026 1:30 pm" for the trigger button.
 *  In dateOnly mode → "05/14/2026" (no time). */
function formatDisplay(value: string, dateOnly = false): string {
  if (!value) return 'Pick a date';
  const p = parseValue(value);
  const date = `${pad2(p.month)}/${pad2(p.day)}/${p.year}`;
  if (dateOnly) return date;
  const ampm = p.hour >= 12 ? 'pm' : 'am';
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${date} ${h12}:${pad2(p.minute)} ${ampm}`;
}

/** Number of days in a given month (1-12). Handles leap years correctly. */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/** Day-of-week (0=Sun..6=Sat) for the 1st of the given month. */
function firstWeekdayOfMonth(year: number, month: number): number {
  return new Date(year, month - 1, 1).getDay();
}

interface DateTimePickerProps {
  /** Current value in "YYYY-MM-DDTHH:MM" format. Empty string is allowed. */
  value: string;
  onChange: (next: string) => void;
  /**
   * Optional. When the user clicks a NEW date (different from the
   * currently-selected one), snap the time portion to this value.
   * Format: "HH:MM". Useful for "from date" pickers that should
   * default to 00:00, or "to date" pickers that should default to
   * 23:59. When unset, the existing time portion is preserved.
   */
  defaultTimeOnDateSelect?: string;
  /** Optional. Disable the trigger button. */
  disabled?: boolean;
  /** Optional. Wrapper className for the trigger. */
  className?: string;
  /** Optional. Placeholder shown when value is empty. */
  placeholder?: string;
  /**
   * Optional. Date-only mode: hides the time sliders and emits a bare
   * `YYYY-MM-DD` value (no `T00:00`). Use for fields that mean a calendar
   * DAY, not an instant (e.g. an auth-list validity window whose server
   * semantics snap to UTC day boundaries). `defaultTimeOnDateSelect` is
   * ignored in this mode.
   */
  dateOnly?: boolean;
}

export function DateTimePicker({
  value,
  onChange,
  defaultTimeOnDateSelect,
  disabled,
  className,
  placeholder = 'Pick a date',
  dateOnly = false,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parseValue(value), [value]);

  // The month the calendar is currently showing — independent of the
  // selected date so the user can navigate months without losing their
  // selection.
  const [view, setView] = useState({ year: parsed.year, month: parsed.month });

  // When the dialog opens (or value changes externally), sync the
  // viewed month to the value's month so the selected day is visible.
  useEffect(() => {
    if (open) setView({ year: parsed.year, month: parsed.month });
  }, [open, parsed.year, parsed.month]);

  const onDateClick = (day: number) => {
    if (dateOnly) {
      onChange(format({ year: view.year, month: view.month, day, hour: 0, minute: 0 }, true));
      setOpen(false);
      return;
    }
    let hour = parsed.hour;
    let minute = parsed.minute;
    if (defaultTimeOnDateSelect) {
      const m = /^(\d{1,2}):(\d{2})$/.exec(defaultTimeOnDateSelect);
      if (m) { hour = Number(m[1]); minute = Number(m[2]); }
    }
    onChange(format({ year: view.year, month: view.month, day, hour, minute }));
  };

  const onTimeChange = (hour: number, minute: number) => {
    onChange(format({ ...parsed, hour, minute }));
  };

  const navMonth = (delta: number) => {
    setView((v) => {
      let m = v.month + delta;
      let y = v.year;
      if (m < 1) { m = 12; y -= 1; }
      else if (m > 12) { m = 1; y += 1; }
      return { year: y, month: m };
    });
  };

  // Build the calendar cells: leading blanks for the first row,
  // then days 1..N. We render rows of 7 in CSS grid so trailing
  // blanks aren't strictly needed.
  const cells = useMemo<Array<number | null>>(() => {
    const lead = firstWeekdayOfMonth(view.year, view.month);
    const total = daysInMonth(view.year, view.month);
    const arr: Array<number | null> = [];
    for (let i = 0; i < lead; i += 1) arr.push(null);
    for (let d = 1; d <= total; d += 1) arr.push(d);
    return arr;
  }, [view.year, view.month]);

  const isSelected = (day: number) =>
    parsed.year === view.year &&
    parsed.month === view.month &&
    parsed.day === day;

  // Today's date — computed once per popover open so it doesn't churn
  // on every keystroke. The calendar grid uses this to give today a
  // distinct ring + accent color so users can land on "today" at a
  // glance, separate from whichever date is currently selected.
  const today = useMemo(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }, [open]);

  const isToday = (day: number) =>
    today.year === view.year &&
    today.month === view.month &&
    today.day === day;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'inline-flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-background text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 opacity-60" />
          <span>{value ? formatDisplay(value, dateOnly) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-3 w-[280px]">
        {/* Month nav header */}
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={() => navMonth(-1)}
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-accent"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-sm font-medium">
            {MONTH_NAMES[view.month - 1]} {view.year}
          </div>
          <button
            type="button"
            onClick={() => navMonth(1)}
            className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-accent"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-0.5">
          {DAY_LABELS.map((d) => (
            <div
              key={d}
              className="text-center text-[10px] font-medium text-muted-foreground py-1"
            >
              {d}
            </div>
          ))}
          {cells.map((day, i) => (
            day === null ? (
              <div key={`b-${i}`} />
            ) : (
              <button
                key={`d-${day}`}
                type="button"
                onClick={() => onDateClick(day)}
                className={cn(
                  'h-7 w-7 text-xs rounded inline-flex items-center justify-center hover:bg-accent transition-colors',
                  // Today: amber outline + text — visible whether or
                  // not it's also the selected date. When today IS
                  // the selected date, the selected (blue bg + white
                  // text) wins, but we still keep a faint amber ring
                  // so the user can tell "today happens to be picked".
                  isToday(day) && !isSelected(day) && 'ring-1 ring-amber-400 text-amber-700 font-semibold',
                  isToday(day) && isSelected(day) && 'ring-2 ring-amber-300 ring-offset-1',
                  isSelected(day) && 'bg-blue-500 text-white hover:bg-blue-500',
                )}
                title={isToday(day) ? 'Today' : undefined}
              >
                {day}
              </button>
            )
          ))}
        </div>

        {/* Time sliders — hidden in date-only mode. */}
        {!dateOnly && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="grid grid-cols-[60px_1fr_60px] items-baseline gap-x-3 mb-2">
            <div className="text-xs font-medium">Time</div>
            <div />
            <div className="text-right text-sm font-mono">
              {minutesToTime12(parsed.hour, parsed.minute)}
            </div>
          </div>
          <SliderRow
            label="Hour"
            min={0}
            max={23}
            value={parsed.hour}
            onChange={(h) => onTimeChange(h, parsed.minute)}
          />
          <SliderRow
            label="Minute"
            min={0}
            max={59}
            value={parsed.minute}
            onChange={(m) => onTimeChange(parsed.hour, m)}
          />
        </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function minutesToTime12(hour: number, minute: number): string {
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

/**
 * Single-thumb slider for hour or minute. Uses HTML5 range input with
 * appearance:none and Tailwind accent so it picks up the project's
 * primary color. Touch + keyboard work via the browser's built-in
 * range semantics; no custom Pointer Event code needed.
 */
function SliderRow({
  label, min, max, value, onChange,
}: {
  label: string; min: number; max: number; value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="grid grid-cols-[60px_1fr] items-center gap-x-3 py-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 w-full appearance-none rounded-full bg-slate-200 dark:bg-slate-700 accent-blue-500 cursor-pointer"
      />
    </div>
  );
}
